import { SetStateAction, useCallback, useRef, useState } from 'react';

import { GridPaginationModel } from '@mui/x-data-grid';

import { usePersistedSetting } from './usePersistedSetting';

const DEFAULT_PAGE_SIZE = 50;

/**
 * Provides a table pagination model that remembers only the user's rows-per-page preference.
 * Page indexes remain local to a list visit so returning to a list always begins at page zero.
 */
export function usePersistedTablePagination(storageKey: string, defaultPageSize: number = DEFAULT_PAGE_SIZE) {
	const [storedPageSize, setStoredPageSize] = usePersistedSetting<number>(storageKey, defaultPageSize);
	const pageSize = Number.isInteger(storedPageSize) && storedPageSize > 0 ? storedPageSize : defaultPageSize;
	const paginationModelRef = useRef<GridPaginationModel>({ page: 0, pageSize });
	const [paginationModel, setPaginationModelState] = useState<GridPaginationModel>(paginationModelRef.current);

	const setPaginationModel = useCallback(
		(value: SetStateAction<GridPaginationModel>) => {
			const current = paginationModelRef.current;
			const next = typeof value === 'function' ? value(current) : value;
			paginationModelRef.current = next;
			if (next.pageSize !== current.pageSize) {
				setStoredPageSize(next.pageSize);
			}
			setPaginationModelState(next);
		},
		[setStoredPageSize],
	);

	return { paginationModel, setPaginationModel };
}
